import React, { FC, useEffect, useRef, useState } from "react";
import Vector from "../../../../assets/GoBackApp.svg";
import "./styles.scss";
import HorizontalScroll from "../horizontal-scroll";
import { useTranslation } from "react-i18next";
import { useStore } from "zustand";
import { dashboardStore } from "entities/dashboard";
import { CURRENCY_FLAGS } from "static/currencies";
import DatePicker from "../date-picker";
import moment from "moment";

type CurrencyBlockType = {
  code: string;
  selected?: boolean;
  onClick: () => void;
};

const RepoCurrencyFilterBlock: FC<CurrencyBlockType> = ({
  code,
  selected,
  onClick,
}) => {
  return (
    <button
      onClick={onClick}
      className={
        selected
          ? "repo-currency-block repo-currency-block-active"
          : "repo-currency-block"
      }
    >
      <img src={CURRENCY_FLAGS[code]} alt="currency" />
      <span>{code}</span>
    </button>
  );
};

type InputBlockType = {
  label: "from" | "to";
  placeholder: string;
  value?: number | undefined;
  onChange?: (e: any) => void;
};

const RepoInputFilterBlock: FC<InputBlockType> = ({
  label,
  placeholder,
  value,
  onChange,
}) => {
  const { t } = useTranslation("app");

  return (
    <div className="repo-input-block">
      <label>{t(label)}</label>
      <input
        type="number"
        placeholder={placeholder}
        value={value || ""}
        onChange={(e) => onChange && onChange(e)}
      />
    </div>
  );
};

type PropsType = {
  opened: boolean;
  onReset: () => void;
  onConfirm: (filter: {
    currency?: string[];
    amount?: number[];
    fromDate?: string | null;
    toDate?: string | null;
  }) => void;
  onClose: () => void;
};

export const RepoTransactionFilter: FC<PropsType> = ({
  opened,
  onReset,
  onConfirm,
  onClose,
}) => {
  const { t, i18n } = useTranslation("app");
  const { currencies } = useStore(dashboardStore);

  const [fromAmount, setFromAmount] = useState<number | undefined>(undefined);
  const [toAmount, setToAmount] = useState<number | undefined>(undefined);

  const formatted = "YYYY.MM.DD";
  const [startDate, setStartDate] = useState<string | null>(null);
  const [endDate, setEndDate] = useState<string | null>(null);

  const [selectedCurrencies, setSelectedCurrencies] = useState<string[]>([]);

  const handleCurrencySelect = (code: string) => {
    setSelectedCurrencies((prevSelected) =>
      prevSelected.includes(code)
        ? prevSelected.filter((item) => item !== code)
        : [...prevSelected, code]
    );
  };

  const handleReset = () => {
    setSelectedCurrencies([]);
    setFromAmount(undefined);
    setToAmount(undefined);
    setStartDate(null);
    setEndDate(null);
  };

  const isFilterEmpty =
    selectedCurrencies.length === 0 &&
    fromAmount === undefined &&
    toAmount === undefined &&
    startDate === null &&
    endDate === null;

  return (
    <div
      className={
        opened
          ? "repo-transaction-filter-component repo-transaction-filter-component-opened"
          : "repo-transaction-filter-component"
      }
    >
      <div className="repo-transaction-filter-header">
        <p>{t("filterOptions")}</p>
        <img onClick={onClose} src={Vector} alt="Close" />
      </div>

      <div className="repo-transaction-currency-filter">
        <p
          style={{
            width:
              i18n.language == "en" || i18n.language == "ru" ? "60px" : "76px",
            minWidth:
              i18n.language == "en" || i18n.language == "ru" ? "60px" : "76px",
          }}
        >
          {t("currency")}
        </p>
        <HorizontalScroll className="repo-transaction-currency-filter-scroll">
          {currencies.map((item) => (
            <RepoCurrencyFilterBlock
              key={item.id}
              code={item.code}
              onClick={() => handleCurrencySelect(item.code)}
              selected={selectedCurrencies.includes(item.code)}
            />
          ))}
        </HorizontalScroll>
      </div>

      <div className="repo-transaction-amount-filter">
        <p
          style={{
            width:
              i18n.language == "en" || i18n.language == "ru" ? "60px" : "76px",
            minWidth:
              i18n.language == "en" || i18n.language == "ru" ? "60px" : "76px",
          }}
        >
          {t("amount")}
        </p>
        <div className="repo-input-filter-block-wrapper">
          <RepoInputFilterBlock
            label="from"
            placeholder="100"
            value={fromAmount || undefined}
            onChange={(e) => setFromAmount(e.target.valueAsNumber || undefined)}
          />
          <RepoInputFilterBlock
            label="to"
            placeholder="1000"
            value={toAmount || undefined}
            onChange={(e) => setToAmount(e.target.valueAsNumber || undefined)}
          />
        </div>
      </div>

      <div className="repo-transaction-amount-filter">
        <p
          style={{
            width:
              i18n.language == "en" || i18n.language == "ru" ? "60px" : "76px",
            minWidth:
              i18n.language == "en" || i18n.language == "ru" ? "60px" : "76px",
          }}
        >
          {t("date")}
        </p>
        <div className="repo-input-filter-block-wrapper">
          <DatePicker
            deadline={t("from")}
            repoLabel={t("from")}
            repoFilter
            selected={startDate ? moment(startDate, formatted).toDate() : null}
            onChange={(date: any) =>
              setStartDate(date ? moment(date).format(formatted) : null)
            }
          />
          <DatePicker
            deadline={t("to")}
            repoLabel={t("to")}
            repoFilter
            selected={endDate ? moment(endDate, formatted).toDate() : null}
            onChange={(date: any) =>
              setEndDate(date ? moment(date).format(formatted) : null)
            }
          />
        </div>
      </div>

      <div className="repo-transaction-filter-footer">
        <button
          onClick={() => {
            onReset();
            handleReset();
          }}
          disabled={isFilterEmpty}
        >
          {t("reset")}
        </button>

        <button
          disabled={isFilterEmpty}
          onClick={() => {
            onConfirm?.({
              currency: selectedCurrencies,
              amount: [fromAmount ?? 0, toAmount ?? Number.MAX_SAFE_INTEGER],
              fromDate: startDate && startDate,
              toDate: endDate && endDate,
            });
          }}
        >
          {t("confirm")}
        </button>
      </div>
    </div>
  );
};
